import { useCallback, useEffect, useMemo, useState } from 'react';
import { T } from '../components/text/TextEdit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, Activity, ShieldAlert, Search, CheckCircle2, XCircle } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SectionCard, Field, Skeleton } from '../components/pipeline/ui';
import CorpusAccessCard from '../components/CorpusAccessCard';
import CorpusDocumentsCard from '../components/CorpusDocumentsCard';

const LANGS = ['en', 'fa', 'es'];

function StatusPill({ ok, labelOk, labelBad, testId }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
        ok ? 'bg-success-soft text-success border-success-border' : 'bg-warning-soft text-warning border-warning-border'
      }`}
      data-testid={testId}
    >
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {ok ? labelOk : labelBad}
    </span>
  );
}

// CA6 (060): many corpus engines, NO default — visibility decides what a user can ground with.
// A superadmin sees everyone's connections; a user sees ONLY his own and may edit/delete only his
// own. The API key is write-only — the server only ever reports key_set, never the value.
function ConnectionsCard({ connections, isSuper = true, onChanged }) {
  const { t } = useTranslation();
  const BLANK = { id: null, label: '', base_url: '', api_key: '' };
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!form.label.trim() || !form.base_url.trim()) {
      toast.error(t('corpusAdmin.connLabelUrlRequired'));
      return;
    }
    setBusy(true);
    try {
      await api.corpusAdmin('upsert_connection', {
        id: form.id || undefined,
        label: form.label.trim(),
        base_url: form.base_url.trim(),
        api_key: form.api_key || undefined,
      });
      toast.success(t('corpusAdmin.connSaved'));
      setForm(BLANK);
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const act = async (action, id, okMsg) => {
    setBusy(true);
    try {
      await api.corpusAdmin(action, { id });
      toast.success(okMsg);
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <SectionCard title={t('corpusAdmin.connectionsTitle')} testId="corpus-connections-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="corpusAdmin.connectionsHint" /></p>

      {connections === null ? <Skeleton className="h-24 w-full" /> : (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm" data-testid="corpus-connections-table">
            <thead>
              <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="text-start py-2 pe-4"><T k="corpusAdmin.connLabel" /></th>
                <th className="text-start py-2 pe-4"><T k="corpusAdmin.connBaseUrl" /></th>
                <th className="text-start py-2 pe-4"><T k="corpusAdmin.connKey" /></th>
                <th className="text-start py-2 pe-4"><T k="corpusAdmin.connOwner" /></th>
                <th className="text-start py-2" />
              </tr>
            </thead>
            <tbody className="font-data">
              {connections.map((c) => (
                <tr key={c.id} className="border-b border-border" data-testid={`corpus-conn-${c.id}`}>
                  <td className="py-2 pe-4 text-foreground">{c.label}</td>
                  <td className="py-2 pe-4 text-muted-foreground truncate max-w-[240px]">{c.base_url}</td>
                  <td className="py-2 pe-4">
                    {c.key_set
                      ? <span className="text-success text-xs"><T k="corpusAdmin.keySetYes" /></span>
                      : <span className="text-warning text-xs"><T k="corpusAdmin.keySetNo" /></span>}
                  </td>
                  {/* CA6: there is no default any more — visibility decides. What matters here is
                      WHOSE corpus this is: the platform's (everyone can ground with it) or mine. */}
                  <td className="py-2 pe-4" data-testid={`corpus-conn-owner-${c.id}`}>
                    {c.is_platform
                      ? <span className="text-xs font-semibold text-foreground"><T k="corpusAdmin.connPlatform" /></span>
                      : <span className="text-xs text-muted-foreground"><T k="corpusAdmin.connMine" /></span>}
                  </td>
                  <td className="py-2 flex gap-2">
                    {(isSuper || !c.is_platform) && (
                      <Button variant="outline" size="sm" disabled={busy}
                        onClick={() => setForm({ id: c.id, label: c.label, base_url: c.base_url, api_key: '' })}
                        data-testid={`corpus-conn-edit-${c.id}`}>
                        {t('corpusAdmin.connEdit')}
                      </Button>
                    )}
                    {/* A user may delete only his own; the platform corpus is not his to remove
                        (the 060 RPC refuses it regardless of what is drawn here). */}
                    {(isSuper || !c.is_platform) && (
                      <Button variant="outline" size="sm" disabled={busy}
                        onClick={() => act('delete_connection', c.id, t('corpusAdmin.connDeleted'))}
                        data-testid={`corpus-conn-delete-${c.id}`}>
                        {t('corpusAdmin.connDelete')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {connections.length === 0 && (
                <tr><td colSpan={5} className="py-3 text-muted-foreground" data-testid="corpus-conn-empty">
                  {t('corpusAdmin.connNone')}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mt-4 border-t border-border pt-4">
        <Field label={<T k="corpusAdmin.connLabel" />}>
          <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
            data-testid="corpus-conn-label-input" />
        </Field>
        <Field label={<T k="corpusAdmin.connBaseUrl" />}>
          <Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })}
            placeholder="https://cloud.ragflow.io" data-testid="corpus-conn-url-input" />
        </Field>
        <Field label={<T k="corpusAdmin.connApiKey" />}>
          <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            placeholder={form.id ? t('corpusAdmin.connKeyKeep') : ''} data-testid="corpus-conn-key-input" />
        </Field>
      </div>
      <div className="mt-3 flex gap-2">
        <Button onClick={save} disabled={busy} data-testid="corpus-conn-save">
          <KeyRound className="me-2 h-4 w-4" />
          {form.id ? t('corpusAdmin.connUpdate') : t('corpusAdmin.connAdd')}
        </Button>
        {form.id && (
          <Button variant="outline" onClick={() => setForm(BLANK)} data-testid="corpus-conn-cancel">
            {t('corpusAdmin.connCancel')}
          </Button>
        )}
      </div>
    </SectionCard>
  );
}

// N-64 — Ali: "i dont see any where that a user can build a dataset himself."
//
// A dataset name is not free text: the read path DERIVES industries from `{lang}__{industry}__{tier}`
// (listIndustries groups datasets by the middle segment), so a corpus named anything else exists on
// the engine and can never surface as something to ground with. Building the name from three fields
// instead of accepting a string is the difference between a dataset that works and one that is
// invisible for reasons nobody can see.
//
// The connection picker offers only what the caller may WRITE to — for a user, his own engines. The
// platform corpus is readable and never writable, and the server refuses it regardless of what is
// drawn here.
export function DatasetsCard({ datasets, connections, presets, embeddingModels, onChanged }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState('fa');
  // N-92: permanent per-dataset choice, so it is made here and never inferred from list order.
  const [embedding, setEmbedding] = useState('');
  const [industry, setIndustry] = useState('');
  const [tier, setTier] = useState('');
  const [preset, setPreset] = useState('standard');
  const [connectionId, setConnectionId] = useState('');
  const [busy, setBusy] = useState(false);
  // N-67: repeal-filtering status per dataset, MEASURED on demand. Not fetched on mount — it costs
  // an engine call per dataset, and an unasked-for cost is the thing this admin surface exists to
  // avoid.
  const [repeal, setRepeal] = useState({});

  const checkRepeal = async (d) => {
    setBusy(true);
    try {
      const r = await api.corpusAdmin('repeal_status', {
        dataset_name: d.name, connection_id: d.connection_id ?? null,
      });
      setRepeal((prev) => ({ ...prev, [d.name]: r }));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  useEffect(() => {
    if (!connectionId && (connections || []).length > 0) setConnectionId(connections[0].id);
  }, [connections, connectionId]);

  // Lowercase throughout: industry grouping is case-sensitive string matching, so `Insurance` and
  // `insurance` would silently become two industries.
  const clean = (v) => v.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  const name = `${lang}__${clean(industry)}__${clean(tier)}`;
  const ready = clean(industry).length > 0 && clean(tier).length > 0 && !!connectionId;

  const create = async () => {
    if (!ready) { toast.error(t('corpusDatasets.needFields')); return; }
    setBusy(true);
    try {
      await api.corpusAdmin('create_dataset', {
        name, preset, connection_id: connectionId,
        ...(embedding ? { embedding_model: embedding } : {}),
      });
      toast.success(t('corpusDatasets.created', { name }));
      setIndustry(''); setTier('');
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const drop = async (d) => {
    if (!window.confirm(t('corpusDatasets.confirmDelete', { name: d.name }))) return;
    setBusy(true);
    try {
      // Send BOTH: the id when the health row has one, and always the name, which the server can
      // resolve on its own. This button 400'd on every click because health rows carried no id.
      await api.corpusAdmin('delete_dataset', {
        dataset_id: d.dataset_id || undefined, dataset_name: d.name,
        connection_id: d.connection_id ?? null,
      });
      toast.success(t('corpusDatasets.deleted'));
      onChanged();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  // Which datasets are MINE to manage? Not "not platform" — that was wrong, and it showed a
  // superadmin an empty card: every connection he owns IS a platform connection, so the law corpus
  // vanished from the one screen built to curate it. The right test is the same one the server
  // applies: is this dataset on a connection I may WRITE to. For a user that excludes the platform
  // corpus; for a superadmin it includes it, because he owns it.
  const writableIds = new Set((connections || []).map((c) => c.id));
  const mine = (datasets || []).filter((d) => writableIds.has(d.connection_id));

  return (
    <SectionCard title={t('corpusDatasets.title')} testId="corpus-datasets-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="corpusDatasets.hint" /></p>
      {/* True regardless of what any check returns, so it is stated unconditionally rather than
          left for the user to infer from a badge. */}
      <p className="text-xs text-muted-foreground mt-1" data-testid="corpus-datasets-repeal-note">
        {t('corpusDatasets.repealNote')}
      </p>

      {mine.length > 0 && (
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm" data-testid="corpus-datasets-table">
            <tbody className="font-data">
              {mine.map((d) => (
                <tr key={d.name} className="border-b border-border" data-testid={`corpus-dataset-${d.name}`}>
                  <td className="py-2 pe-4 text-foreground">{d.name}</td>
                  <td className="py-2 pe-4 text-muted-foreground">{d.connection_label || '—'}</td>
                  <td className="py-2 pe-4 text-muted-foreground">
                    {d.chunk_count == null ? '—' : t('corpusDatasets.chunks', { n: d.chunk_count })}
                  </td>
                  {/* N-67: the honest label. A corpus that does not carry a repeal marker gets a
                      filter that matches nothing — which looks exactly like a corpus with nothing
                      repealed in it. Never state it as a fact until it has been measured, so the
                      unchecked state says "unknown" rather than guessing either way. */}
                  <td className="py-2 pe-4" data-testid={`corpus-dataset-repeal-${d.name}`}>
                    {!repeal[d.name] ? (
                      <Button size="sm" variant="outline" disabled={busy}
                        onClick={() => checkRepeal(d)} data-testid={`corpus-dataset-repeal-check-${d.name}`}>
                        {t('corpusDatasets.repealCheck')}
                      </Button>
                    ) : repeal[d.name].uses_convention ? (
                      <span className="text-xs text-success">
                        {t('corpusDatasets.repealAuto', { n: repeal[d.name].marked })}
                      </span>
                    ) : (
                      <span className="text-xs text-warning">
                        {repeal[d.name].checked === 0
                          ? t('corpusDatasets.repealUnknown')
                          : t('corpusDatasets.repealManual')}
                      </span>
                    )}
                  </td>
                  <td className="py-2">
                    <Button size="sm" variant="outline" disabled={busy}
                      onClick={() => drop(d)} data-testid={`corpus-dataset-delete-${d.name}`}>
                      {t('corpusDatasets.delete')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(connections || []).length === 0 ? (
        <p className="text-sm text-warning mt-3" data-testid="corpus-datasets-noconn">
          {t('corpusDatasets.needConnection')}
        </p>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 border-t border-border pt-4">
            <Field label={<T k="corpusDatasets.connection" />}>
              <select className="w-full text-sm border border-border rounded-md px-2 py-2 bg-card text-foreground"
                value={connectionId} onChange={(e) => setConnectionId(e.target.value)}
                data-testid="corpus-dataset-connection">
                {(connections || []).map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label={<T k="corpusDatasets.lang" />}>
              <select className="w-full text-sm border border-border rounded-md px-2 py-2 bg-card text-foreground"
                value={lang} onChange={(e) => setLang(e.target.value)} data-testid="corpus-dataset-lang">
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <Field label={<T k="corpusDatasets.industry" />}>
              <Input value={industry} onChange={(e) => setIndustry(e.target.value)}
                placeholder="insurance" data-testid="corpus-dataset-industry" />
            </Field>
            <Field label={<T k="corpusDatasets.tier" />}>
              <Input value={tier} onChange={(e) => setTier(e.target.value)}
                placeholder="ratifications" data-testid="corpus-dataset-tier" />
            </Field>
            <Field label={<T k="corpusDatasets.preset" />}>
              <select className="w-full text-sm border border-border rounded-md px-2 py-2 bg-card text-foreground"
                value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="corpus-dataset-preset">
                {(presets || []).map((p) => (
                  <option key={p.key} value={p.key}>{p.label} ({p.cost_tier}x)</option>
                ))}
              </select>
            </Field>
            {/* N-92: the embedding model decides retrieval quality and CANNOT be changed once
                documents are indexed. Offered as a real choice from the models this account
                already uses; blank keeps the previous behaviour (most-used wins). */}
            <Field label={<T k="corpusDatasets.embedding" />}>
              <select className="w-full text-sm border border-border rounded-md px-2 py-2 bg-card text-foreground"
                value={embedding} onChange={(e) => setEmbedding(e.target.value)}
                data-testid="corpus-dataset-embedding">
                <option value="">{t('corpusDatasets.embeddingAuto')}</option>
                {(embeddingModels || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
          </div>
          <p className="text-xs text-muted-foreground mt-2"><T k="corpusDatasets.embeddingHint" /></p>
          {/* The name is shown before the click, not after: it is derived, and a derived value the
              user cannot see is a value they cannot correct. */}
          <p className="text-xs text-muted-foreground mt-3 font-data" data-testid="corpus-dataset-preview">
            {t('corpusDatasets.willCreate')} <span className="text-foreground">{name}</span>
          </p>
          <div className="mt-3">
            <Button onClick={create} disabled={busy || !ready} data-testid="corpus-dataset-create">
              {t('corpusDatasets.create')}
            </Button>
          </div>
        </>
      )}
    </SectionCard>
  );
}

function HealthCard({ health, loading, onRun, connections, selected, onSelect }) {
  const { t } = useTranslation();
  const invalid = (health?.datasets || []).filter((d) => !d.valid);
  // Ali, 2026-08-14: you must be able to test A SPECIFIC engine. Adding a connection you
  // cannot verify is a dead end, and with several connected, an aggregate 'reachable' hides
  // which one is broken. A user sees his own here; a superadmin sees everyone's, which is how
  // a customer's engine gets diagnosed without asking the customer to debug it.
  const options = connections || [];
  return (
    <SectionCard
      title={t('corpusAdmin.healthTitle')}
      testId="corpus-health-card"
      action={
        <div className="flex items-center gap-2">
          <select
            className="text-xs border border-border rounded-md px-2 py-1.5 bg-card text-foreground"
            value={selected || ''}
            onChange={(e) => onSelect(e.target.value || null)}
            data-testid="corpus-health-connection"
          >
            <option value=""><T k="corpusAdmin.healthAllConnections" /></option>
            {options.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}{c.is_platform ? ` — ${t('corpusAdmin.connPlatform')}` : ''}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={onRun} disabled={loading} data-testid="corpus-health-run"><Activity className="h-4 w-4 me-2" /> {loading ? t('corpusAdmin.checking') : t('corpusAdmin.runCheck')}</Button>
        </div>
      }
    >
      {!health ? (
        <p className="text-sm text-muted-foreground" data-testid="corpus-health-empty"><T k="corpusAdmin.healthEmpty" /></p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <StatusPill ok={health.reachable} labelOk={t('corpusAdmin.reachable')} labelBad={t('corpusAdmin.unreachable')} testId="engine-reachable-pill" />
            {health.counts && (
              <span className="text-sm text-muted-foreground font-data" data-testid="dataset-counts">
                {t('corpusAdmin.datasetCounts', { total: health.counts.total, valid: health.counts.valid, invalid: health.counts.invalid })}
              </span>
            )}
          </div>
          {!health.reachable && health.error && (
            <div className="flex items-start gap-3 rounded-md border border-warning-border bg-warning-soft p-4 text-foreground dark:bg-warning-soft" data-testid="engine-error">
              <ShieldAlert className="h-5 w-5 shrink-0 mt-0.5 text-warning dark:text-warning" />
              <p className="text-sm leading-relaxed font-data">{health.error}</p>
            </div>
          )}
          {health.datasets?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="datasets-table">
                <thead>
                  <tr className="text-start text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="text-start py-2 pe-4"><T k="corpusAdmin.colDataset" /></th>
                    <th className="text-start py-2 pe-4"><T k="corpusAdmin.colLang" /></th>
                    <th className="text-start py-2 pe-4"><T k="corpusAdmin.colIndustry" /></th>
                    <th className="text-start py-2 pe-4"><T k="corpusAdmin.colTier" /></th>
                    <th className="text-start py-2 pe-4"><T k="corpusAdmin.colChunks" /></th>
                    <th className="text-start py-2 pe-4"><T k="corpusAdmin.colParse" /></th>
                    <th className="text-start py-2"><T k="corpusAdmin.colValid" /></th>
                  </tr>
                </thead>
                <tbody className="font-data">
                  {health.datasets.map((d) => (
                    <tr key={d.name} className="border-b border-border" data-testid={`dataset-row-${d.name}`}>
                      <td className="py-2 pe-4 text-foreground">{d.name}</td>
                      <td className="py-2 pe-4">{d.lang || '—'}</td>
                      <td className="py-2 pe-4">{d.industry || '—'}</td>
                      <td className="py-2 pe-4">{d.tier || '—'}</td>
                      <td className="py-2 pe-4">{d.chunk_count ?? '—'}</td>
                      <td className="py-2 pe-4">{d.parse_status ? `${d.parse_status.success}✓ / ${d.parse_status.fail}✗ / ${d.parse_status.processing}…` : '—'}</td>
                      <td className="py-2">{d.valid ? <CheckCircle2 className="h-4 w-4 text-success" /> : <span className="text-warning">✗</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="border-t border-border pt-4">
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground mb-2"><T k="corpusAdmin.namingTitle" /></p>
            {invalid.length === 0 ? (
              <p className="text-sm text-success" data-testid="naming-all-valid"><T k="corpusAdmin.namingAllValid" /></p>
            ) : (
              <ul className="space-y-1" data-testid="naming-invalid-list">
                {invalid.map((d) => (
                  <li key={d.name} className="text-sm text-warning font-data">{d.name} — {d.reason}</li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </SectionCard>
  );
}

function RetrievalTestCard({ industriesByLang }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState('en');
  const [industry, setIndustry] = useState('');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const options = industriesByLang[lang] || [];

  const run = async () => {
    if (!query.trim()) { toast.error(t('corpusAdmin.queryRequired')); return; }
    setBusy(true);
    try {
      const r = await api.corpusAdmin('retrieval_test', { query: query.trim(), lang, industry_key: industry });
      setResult(r);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SectionCard title={t('corpusAdmin.retrievalTitle')} testId="corpus-retrieval-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="corpusAdmin.retrievalHint" /></p>
      {/* Two-up, as the mock draws it. The button used to be a third column here, which put
          it BESIDE the selects and above the query - the one field it actually acts on. */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={<T k="corpusAdmin.language" />}>
          <Select value={lang} onValueChange={(v) => { setLang(v); setIndustry(''); }}>
            <SelectTrigger data-testid="retrieval-lang-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LANGS.map((l) => <SelectItem key={l} value={l} data-testid={`retrieval-lang-${l}`}>{t(`language.${l}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label={<T k="corpusAdmin.industry" />}>
          <Select value={industry || 'none'} onValueChange={(v) => setIndustry(v === 'none' ? '' : v)}>
            <SelectTrigger data-testid="retrieval-industry-select"><SelectValue placeholder={t('corpusAdmin.industryNone')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" data-testid="retrieval-industry-none"><T k="corpusAdmin.industryNone" /></SelectItem>
              {options.map((ind) => <SelectItem key={ind} value={ind} data-testid={`retrieval-industry-${ind}`}>{ind}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>
      <Field label={<T k="corpusAdmin.query" />}>
        <Textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('corpusAdmin.queryPlaceholder')}
          className="min-h-[80px]"
          data-testid="retrieval-query-input"
        />
      </Field>

      {/* Last, and end-aligned: the action comes after everything it reads. */}
      <div className="flex justify-end">
        <Button onClick={run} disabled={busy} data-testid="retrieval-run">
          <Search className="me-2 h-4 w-4" />
          {busy ? t('corpusAdmin.previewing') : t('corpusAdmin.preview')}
        </Button>
      </div>
      {result && (
        <div className="border-t border-border pt-4" data-testid="retrieval-result">
          <p className="text-sm text-muted-foreground mb-3 font-data">
            {t('corpusAdmin.resultMeta', { datasets: result.dataset_count, chunks: result.chunks?.length || 0 })}
            {result.note ? ` — ${result.note}` : ''}
          </p>
          {result.engine_failed ? (
            /* The engine failed — say so. Previously this rendered the same "no chunks" line as a
               genuinely empty search, which is how a three-day outage stayed invisible. */
            <p className="text-sm text-destructive" data-testid="retrieval-engine-failed">
              {t('corpusAdmin.engineFailed')}{result.error ? ` — ${result.error}` : ''}
            </p>
          ) : (!result.chunks || result.chunks.length === 0) ? (
            <p className="text-sm text-warning" data-testid="retrieval-no-chunks"><T k="corpusAdmin.noChunks" /></p>
          ) : (
            <ul className="space-y-3">
              {result.chunks.map((c, i) => (
                <li key={`${c.source_url || c.source_title || 'chunk'}-${i}`} className="border border-border rounded-md p-4" data-testid={`retrieval-chunk-${i}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-xs font-data text-muted-foreground truncate">{c.source_title || t('corpusAdmin.untitled')}{c.date ? ` · ${c.date}` : ''}</span>
                    <span className="font-data text-sm font-semibold text-foreground">{typeof c.score === 'number' ? c.score.toFixed(3) : c.score}</span>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed">{c.text}</p>
                  {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="text-xs text-primary underline font-data">{c.source_url}</a>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SectionCard>
  );
}

export default function CorpusAdminPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [allowed, setAllowed] = useState(null);
  const [isSuper, setIsSuper] = useState(false);
  const [connections, setConnections] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthConn, setHealthConn] = useState(null);
  // N-64: where may this caller WRITE? For a user that is his own engines only — the platform
  // corpus is readable and never writable, so it must not appear in a create/upload picker.
  const [writable, setWritable] = useState([]);
  const [presets, setPresets] = useState([]);
  const [embeddingModels, setEmbeddingModels] = useState([]);


  // Tolerates an install where 052 has not been applied yet: the card simply shows no rows.
  const loadConnections = useCallback(() => {
    api.corpusAdmin('list_connections')
      .then((r) => setConnections(r.connections || []))
      .catch(() => setConnections([]));
    api.corpusAdmin('writable_connections')
      .then((r) => setWritable(r.connections || []))
      .catch(() => setWritable([]));
    api.corpusAdmin('ingest_presets')
      .then((r) => { setPresets(r.presets || []); setEmbeddingModels(r.embedding_models || []); })
      .catch(() => setPresets([]));
  }, []);

  const runHealth = useCallback(async (connectionId = null) => {
    setHealthLoading(true);
    try { setHealth(await api.corpusAdmin('health', connectionId ? { connection_id: connectionId } : {})); }
    catch (e) { toast.error(e.message); }
    finally { setHealthLoading(false); }
  }, []);

  // CA6: this page is no longer super-admin-only. A USER reaches it to manage HIS OWN corpus
  // connections — that is the point of "bring your own RAGFlow". He sees the connections card and
  // the health card (scoped server-side to his own engines); corpus access stays super-admin.
  // The server enforces the same split (corpus-admin's OWN_CONNECTION_ACTIONS + the 060 RPCs),
  // so this decides what is DRAWN, never what is permitted.
  // N-87: health now auto-runs for EVERYONE — users build datasets since N-64, so the check can
  // succeed, and the retrieval self-test below needs its industriesByLang to offer anything.
  useEffect(() => {
    api.getMe().then((r) => {
      setIsSuper(!!r.is_admin);
      setAllowed(true);
      loadConnections();
      runHealth();
    }).catch(() => { setAllowed(false); navigate('/'); });
  }, [navigate, loadConnections, runHealth]);

  const industriesByLang = useMemo(() => {
    const map = {};
    for (const d of (health?.datasets || [])) {
      if (d.valid && d.lang && d.industry) {
        (map[d.lang] = map[d.lang] || new Set()).add(d.industry);
      }
    }
    return Object.fromEntries(Object.entries(map).map(([k, v]) => [k, [...v]]));
  }, [health]);

  if (allowed === null) {
    return <Skeleton className="h-10 w-48" />;
  }
  if (allowed === false) return null;

  return (
    <>
      <h1 className="font-heading text-3xl font-bold">
        {isSuper ? t('corpusAdmin.title') : t('corpusAdmin.myCorporaTitle')}
      </h1>
      <div className="mt-5 border-t border-border" />

      <div className="mt-5 flex flex-col gap-6" data-testid="corpus-admin-page">
        {!isSuper && (
          <p className="text-sm text-muted-foreground" data-testid="corpus-my-hint">
            {t('corpusAdmin.myCorporaHint')}
          </p>
        )}
        <ConnectionsCard
          connections={connections}
          isSuper={isSuper}
          onChanged={() => { loadConnections(); setHealthConn(null); runHealth(null); }}
        />
        {/* Super-admin only: engine config, dataset health, the documents/ingest surface, the
            retrieval probe and per-industry corpus pricing are platform controls. A user manages
            his connection and nothing else. */}
        {/* N-60: موتور چارچوب صنعت is RETIRED. It edited the 019 singleton, which the read path
            stopped consulting once a platform connection existed (CA6/060) — so an admin could
            change the base URL here and retrieval would not move. Connections are the one place
            an engine is configured now. The server RPC and the env fallback both stay, so an
            install with no platform connection yet still works; only this control is gone. */}
        {/* Shown to everyone now: a user needs to verify the engine he just added. The server
            scopes the list to his own connections. */}
        <HealthCard
          health={health}
          loading={healthLoading}
          connections={connections}
          selected={healthConn}
          onSelect={(id) => { setHealthConn(id); runHealth(id); }}
          onRun={() => runHealth(healthConn)}
        />
        {/* N-64: both cards render for every user now. The server scopes what they can reach —
            a user's health check only returns his own datasets, and every write is
            ownership-checked in the database (062), so the UI decides what is DRAWN and never
            what is permitted. */}
        <DatasetsCard
          datasets={health?.datasets || []}
          connections={writable}
          presets={presets}
          embeddingModels={embeddingModels}
          onChanged={() => runHealth(healthConn)}
        />
        <CorpusDocumentsCard datasets={health?.datasets || []} />
        {/* N-87: a user who can BUILD a dataset must be able to SEE what retrieval returns from
            it — the server action is caller-scoped since N-66, so drawing it for everyone opens
            nothing the server does not already permit. */}
        <RetrievalTestCard industriesByLang={industriesByLang} />
        {isSuper && <CorpusAccessCard industriesByLang={industriesByLang} />}
      </div>
    </>
  );
}
